import { useEffect, useMemo, useState } from 'react';
import TopNav from '@/components/TopNav';
import { AuthorSignature } from '@/components/AuthorSignature';
import { DronePanel } from '@/components/tuner/DronePanel';
import { TunerClick } from '@/components/tuner/TunerClick';
import { ScaleTracker } from '@/components/tuner/ScaleTracker';
import { IntervalTrainer } from '@/components/tuner/IntervalTrainer';
import { IntervalPill } from '@/components/tuner/IntervalPill';
import { SharedExerciseBanner } from '@/components/tuner/SharedExerciseBanner';
import { DroneNowNext } from '@/components/drone/DroneNowNext';
import { SharedSongBanner } from '@/components/drone/SharedSongBanner';
import { readSharedSong, applySong } from '@/lib/droneSongs';
import { MicStatus } from '@/components/drone/MicStatus';
import { Toaster, toast } from '@/components/ui/sonner';
import { useDrone } from '@/hooks/useDrone';
import { useScales } from '@/hooks/useScales';
import { usePitchDetector } from '@/hooks/usePitchDetector';
import { describePitch, describeInterval, freqToMidiFloat } from '@/lib/pitch';
import { readSharedExercise, clearSharedTuningParam, applySharedSettings } from '@/lib/tuningShare';

export default function DronePage() {
  const drone = useDrone();
  const { a4, semitones, flats } = drone;
  const scales = useScales();
  const [training, setTraining] = useState(false);
  const [scaleRunning, setScaleRunning] = useState(false);
  // Choosing a scale starts listening right away; "Nessuna scala" ends it.
  useEffect(() => { setScaleRunning(scales.selectedId !== 'off'); }, [scales.selectedId]);
  const [sharedExercise, setSharedExercise] = useState(() => readSharedExercise(window.location.search));
  const [exerciseVersion, setExerciseVersion] = useState(0);
  const [sharedSong, setSharedSong] = useState(() => readSharedSong(window.location.search));
  const { status, freq, start, stop } = usePitchDetector({ echoCancellation: drone.active });
  // Mic only while a scale is being tracked or an interval round runs.
  const needMic = scaleRunning || training;
  useEffect(() => { if (needMic) start(); else stop(); }, [needMic, start, stop]);

  const pitch = useMemo(() => (freq ? describePitch(freq, a4, semitones, flats) : null), [freq, a4, semitones, flats]);
  const interval = drone.active && freq ? describeInterval(freqToMidiFloat(freq, a4), drone.concertMidi, drone.just) : null;

  const acceptShared = () => {
    const { song } = sharedExercise;
    drone.update({
      ...sharedExercise.drone,
      prog: song ? { ...drone.prog, enabled: true, steps: song.steps, keyPc: sharedExercise.drone.pc, bpmSource: 'click' } : { ...drone.prog, enabled: false },
      ...(song ? { timbre: song.timbre, voicing: song.voicing } : {}),
    });
    if (sharedExercise.scale) scales.importScale(sharedExercise.scale);
    else scales.select('off');
    applySharedSettings(sharedExercise);
    setExerciseVersion((v) => v + 1);
    toast.success(`Esercizio "${sharedExercise.scale?.name || 'Intervalli'}" pronto: avvia il bordone`);
    setSharedExercise(null);
    clearSharedTuningParam();
  };

  return (
    <div className={`relative min-h-screen text-white ${drone.active ? 'pb-24' : 'pb-8'}`} data-testid="drone-root">
      <div className="bg-foam" />
      <div className="bg-grain" />
      <div className="relative z-10 max-w-6xl mx-auto px-3 sm:px-4 md:px-8 py-3 md:py-6 flex flex-col gap-4 sm:gap-6">
        <TopNav />
        <header className="px-1">
          <div className="text-[10px] uppercase tracking-[0.4em] text-white/40">Drone · nota di riferimento e accordi</div>
          <h1 className="font-bebas text-4xl sm:text-5xl lg:text-6xl leading-none mt-1">Bordone<span className="text-sky-400">.</span></h1>
        </header>
        {sharedExercise && (
          <SharedExerciseBanner shared={sharedExercise} flats={flats} onAccept={acceptShared} onDismiss={() => { setSharedExercise(null); clearSharedTuningParam(); }} />
        )}
        {sharedSong && (
          <SharedSongBanner
            song={sharedSong}
            onAccept={() => {
              applySong(drone, sharedSong);
              toast.success(`Sequenza "${sharedSong.name || 'Bordone a tempo'}" caricata: premi Play a tempo`);
              setSharedSong(null);
              clearSharedTuningParam();
            }}
            onDismiss={() => { setSharedSong(null); clearSharedTuningParam(); }}
          />
        )}
        <DroneNowNext drone={drone} />
        <div className="grid grid-cols-[minmax(0,1fr)] lg:grid-cols-[minmax(0,1fr)_minmax(0,22rem)] gap-4 sm:gap-6 items-start">
          <DronePanel drone={drone} flats={flats} />
          <div className="lg:sticky lg:top-24"><TunerClick key={`click-${exerciseVersion}`} /></div>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <MicStatus status={status} needed={needMic} />
          {drone.active && needMic && <IntervalPill interval={interval} label={drone.label} />}
        </div>
        <ScaleTracker drone={drone} interval={interval} pitch={pitch} flats={flats} scales={scales} running={scaleRunning} onRunningChange={setScaleRunning} />
        <IntervalTrainer key={`trainer-${exerciseVersion}`} drone={drone} interval={interval} pitch={pitch} flats={flats} scale={scales.scale} onPlayingChange={setTraining} />
        <footer className="mt-6 mb-4"><AuthorSignature /></footer>
      </div>
      <Toaster position="bottom-right" />
    </div>
  );
}
